import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { parseISO, format } from 'date-fns';
import { isDate } from 'lodash-es';
import type { DateSelection, DateRangeSelection } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useMessagesV2State } from '../../MessagesV2State/MessagesV2State';
import type { MessagesV2SearchValues } from '../../MessagesV2.types';

// startCreateDate / endCreateDate stay ISO yyyy-MM-dd strings (buildCriteria + CSV export untouched).
// These convert between the stored ISO string and the DatePickerInput's Date | null value.
export function isoToDate(iso: string): DateSelection {
  return iso ? parseISO(iso) : null;
}

export function dateToIso(value: DateSelection | DateRangeSelection): string {
  return isDate(value) ? format(value, 'yyyy-MM-dd') : '';
}

function fmtPhoneDigits(d: string): string {
  if (!d) return '';
  if (d.length <= 3) return '(' + d;
  if (d.length <= 6) return '(' + d.slice(0, 3) + ') ' + d.slice(3);
  return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
}

export function parsePhone(raw: string): { phoneDisplay: string; phoneE164: string } {
  if (raw.startsWith('+')) {
    return { phoneDisplay: raw, phoneE164: raw };
  }
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  return {
    phoneDisplay: fmtPhoneDigits(digits),
    phoneE164: digits.length === 10 ? '+1' + digits : '',
  };
}

// Each one scopes the search on its own. `stationeryOrderId` is cross-table like `messageBatchId`, so
// email_service accepts it as the sole identifier and searches email and SMS by it.
const SCOPING_FIELDS = ['email', 'fromEmail', 'replyTo', 'phoneE164', 'eventId', 'personId', 'guestKey', 'messageBatchId', 'stationeryOrderId'] as const;

export function hasScopingIdentifier(v: MessagesV2SearchValues): boolean {
  return SCOPING_FIELDS.some(f => !!v[f]);
}

export const INITIAL_VALUES: MessagesV2SearchValues = {
  email: '',
  fromEmail: '',
  replyTo: '',
  phoneDisplay: '',
  phoneE164: '',
  eventId: '',
  personId: '',
  guestKey: '',
  messageBatchId: '',
  stationeryOrderId: '',
  messageType: '',
  channel: 'all',
  startCreateDate: '',
  endCreateDate: '',
  pageSize: 25,
};

// Returns an array of "label: value" summary chips for non-default fields.
export function buildSummaryChips(v: MessagesV2SearchValues): string[] {
  const chips: string[] = [];
  if (v.email) chips.push(`to email: ${v.email}`);
  if (v.fromEmail) chips.push(`from email: ${v.fromEmail}`);
  if (v.replyTo) chips.push(`reply-to: ${v.replyTo}`);
  if (v.phoneDisplay) chips.push(`to phone: ${v.phoneDisplay}`);
  if (v.eventId) chips.push(`event: ${v.eventId}`);
  if (v.personId) chips.push(`person: ${v.personId}`);
  if (v.guestKey) chips.push(`guest: ${v.guestKey}`);
  if (v.messageBatchId) chips.push(`batch: ${v.messageBatchId}`);
  if (v.stationeryOrderId) chips.push(`card order: ${v.stationeryOrderId}`);
  if (v.messageType) chips.push(`type: ${v.messageType}`);
  if (v.channel !== 'all') chips.push(`channel: ${v.channel}`);
  if (v.startCreateDate) chips.push(`from: ${v.startCreateDate}`);
  if (v.endCreateDate) chips.push(`to: ${v.endCreateDate}`);
  if (v.pageSize !== 25) chips.push(`limit: ${v.pageSize}`);
  return chips;
}

export const useSearchFormController = () => {
  const { runSearch, called, changePageSize } = useMessagesV2State();
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string().email(tq`Please enter a valid email`),
        fromEmail: Yup.string().email(tq`Please enter a valid email`),
        replyTo: Yup.string().email(tq`Please enter a valid email`),
      }),
    // tq is stable (hook returns same reference); listed to satisfy exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formik = useFormik<MessagesV2SearchValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: values => {
      runSearch(values);
    },
  });

  // Auto-collapse once first results arrive.
  useEffect(() => {
    if (called) setIsCollapsed(true);
  }, [called]);

  const gateOk = hasScopingIdentifier(formik.values);
  const emailError = formik.touched.email && formik.errors.email ? formik.errors.email : undefined;
  const fromEmailError = formik.touched.fromEmail && formik.errors.fromEmail ? formik.errors.fromEmail : undefined;
  const replyToError = formik.touched.replyTo && formik.errors.replyTo ? formik.errors.replyTo : undefined;
  const summaryChips = buildSummaryChips(formik.values);

  const { setFieldValue } = formik;

  const handlePhoneChange = useCallback(
    (raw: string) => {
      const { phoneDisplay, phoneE164 } = parsePhone(raw);
      setFieldValue('phoneDisplay', phoneDisplay);
      setFieldValue('phoneE164', phoneE164);
    },
    [setFieldValue],
  );

  const handlePageSizeChange = useCallback(
    (s: number) => {
      setFieldValue('pageSize', s);
      if (called) changePageSize(s);
    },
    [setFieldValue, called, changePageSize],
  );

  const handleStartDateChange = useCallback(
    (value: DateSelection | DateRangeSelection) => {
      setFieldValue('startCreateDate', dateToIso(value));
    },
    [setFieldValue],
  );

  const handleEndDateChange = useCallback(
    (value: DateSelection | DateRangeSelection) => {
      setFieldValue('endCreateDate', dateToIso(value));
    },
    [setFieldValue],
  );

  const handleStartDateClear = useCallback(
    (raw: string) => {
      if (raw === '') setFieldValue('startCreateDate', '');
    },
    [setFieldValue],
  );

  const handleEndDateClear = useCallback(
    (raw: string) => {
      if (raw === '') setFieldValue('endCreateDate', '');
    },
    [setFieldValue],
  );

  const today = useMemo(() => new Date(), []);

  return {
    formik,
    gateOk,
    emailError,
    fromEmailError,
    replyToError,
    isCollapsed,
    setIsCollapsed,
    summaryChips,
    handlePhoneChange,
    handlePageSizeChange,
    handleStartDateChange,
    handleEndDateChange,
    handleStartDateClear,
    handleEndDateClear,
    today,
  };
};
